import {
  MessageId,
  type SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { REDO_RINGTONE } from "@redotech/redo-web/sound/sounds";
import { usePhoneWidget } from "@redotech/redo-web/voice/shared/phone-widget-context";
import { Audio } from "@telnyx/react-client";
import { TelnyxRTC } from "@telnyx/webrtc";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  useAddAgentToCall,
  useAddPhoneNumberToCall,
  useGetConferenceParticipants,
  useGetTelnyxTelephonyToken,
  useLookupCustomerByPhone,
  usePrepareOutboundCall,
  useSetCallHold,
  useSetCallRecording,
  useSetVoiceAvailability,
  type ConferenceParticipant,
} from "./use-voice-actions";
import {
  VOICE_CALL_CUSTOMER_EVENT,
  type VoiceCallCustomerDetail,
} from "./voice-call-customer-event";
import {
  VoiceContextV2,
  type ActiveCallV2,
  type AvailabilityStatus,
  type CallCustomer,
  type ConnectionStatus,
  type DebugTelnyxNotification,
  type IncomingCallV2,
  type MicPermission,
  type TelnyxCallLike,
  type VoiceV2State,
} from "./voice-context-v2";

const TERMINAL_CALL_STATES = new Set(["hangup", "destroy", "purge"]);

const HEARTBEAT_INTERVAL_MS = 20_000;
const MAX_DEBUG_TELNYX_NOTIFICATIONS = 12;

const getPeerConnection = (
  call: TelnyxCallLike & {
    readonly peer?: { readonly instance?: RTCPeerConnection };
    readonly rtcPeerConnection?: RTCPeerConnection;
  },
): RTCPeerConnection | null =>
  call.peer?.instance ?? call.rtcPeerConnection ?? null;

interface ProviderProps {
  readonly children: ReactNode;
}

/**
 * Owns the Telnyx WebRTC session, all voice RPCs, microphone permission
 * state, ringtone playback, and the active/incoming call state machine.
 * One provider instance == one Telnyx session, so the dev harness mounts
 * N providers (one per panel) to test multiple legs in one tab; the
 * production floating widget mounts exactly one.
 *
 * All effect-api calls go through `use-voice-actions.ts` — this file
 * does not call `effectApi.runPromise` directly.
 */
export const VoiceContextV2Provider = memo(function VoiceContextV2Provider({
  children,
}: ProviderProps) {
  // ── RPC handlers ──────────────────────────────────────────────────────
  const getTelnyxToken = useGetTelnyxTelephonyToken();
  const setVoiceAvailability = useSetVoiceAvailability();
  const prepareOutboundCall = usePrepareOutboundCall();
  const setCallHold = useSetCallHold();
  const setCallRecording = useSetCallRecording();
  const lookupCustomerByPhone = useLookupCustomerByPhone();
  const addAgentToCallRpc = useAddAgentToCall();
  const addPhoneNumberToCallRpc = useAddPhoneNumberToCall();
  const getConferenceParticipants = useGetConferenceParticipants();
  const { show: showWidget } = usePhoneWidget();

  // ── Local state ──────────────────────────────────────────────────────
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("disconnected");
  const [desiredOnline, setDesiredOnline] = useState(false);
  const [statusDetail, setStatusDetail] = useState<string | null>(null);
  const [micPermission, setMicPermission] = useState<MicPermission>("unknown");
  const [micError, setMicError] = useState<string | null>(null);
  const [incoming, setIncoming] = useState<IncomingCallV2 | null>(null);
  const [activeCall, setActiveCall] = useState<ActiveCallV2 | null>(null);
  const [muted, setMuted] = useState(false);
  const [deafened, setDeafened] = useState(false);
  const [onHold, setOnHold] = useState(false);
  const [recording, setRecording] = useState(false);
  const [availability, setAvailability] = useState<AvailabilityStatus | null>(
    null,
  );
  const [targetMongoUserId, setTargetMongoUserId] = useState<
    MongoUserId | undefined
  >(undefined);
  const [remoteStream, setRemoteStream] = useState<MediaStream | undefined>(
    undefined,
  );
  const [participants, setParticipants] = useState<
    readonly ConferenceParticipant[]
  >([]);
  const [activeSupportTicketId, setActiveSupportTicketId] =
    useState<SupportTicketId | null>(null);
  const [dialerNumber, setDialerNumber] = useState("");
  const [widgetExpanded, setWidgetExpanded] = useState(false);
  const [debugTelnyxNotifications, setDebugTelnyxNotifications] = useState<
    readonly DebugTelnyxNotification[]
  >([]);
  // When the operator dials while offline we can't place the call
  // immediately — the Telnyx session has to come up first. Stashing
  // the number here lets a `useEffect` watching `connectionStatus`
  // fire the dial as soon as the session is ready.
  const [pendingOutboundNumber, setPendingOutboundNumber] = useState<
    string | null
  >(null);
  // True from the moment Call is clicked until the active-call UI is
  // up (or the dial fails). Lets the dialer button show "Calling…"
  // and stay disabled, so impatient operators don't double-click and
  // queue a second call.
  const [isPlacingOutbound, setIsPlacingOutbound] = useState(false);
  const [transferRinging, setTransferRinging] = useState<{
    readonly targetLabel: string;
  } | null>(null);
  const transferRingingClearTimerRef = useRef<number | null>(null);

  // The authenticated user; we compare their mongoId against the
  // `forwarded_phone` participant kind so the agent's *own* phone leg
  // toggles the local mute + deafen.
  const user = useRequiredContext(UserContext);

  // ── Refs ─────────────────────────────────────────────────────────────
  const telnyxRef = useRef<TelnyxRTC | null>(null);
  const callRef = useRef<TelnyxCallLike | null>(null);
  const statsPollRef = useRef<number | null>(null);
  const debugNotificationIdRef = useRef(0);
  // Tracks the messageId we've already auto-muted for so we don't keep
  // re-toggling on every poll, and so manual un-mute by the agent
  // sticks.
  const autoMutedForMessageIdRef = useRef<MessageId | null>(null);

  // ── Microphone permission watcher ─────────────────────────────────────
  useEffect(() => {
    let permissionStatus: PermissionStatus | null = null;
    let cancelled = false;
    const onChange = () => {
      if (cancelled || !permissionStatus) {
        return;
      }
      setMicPermission(permissionStatus.state as MicPermission);
    };
    (async () => {
      try {
        permissionStatus = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        if (cancelled) {
          return;
        }
        setMicPermission(permissionStatus.state as MicPermission);
        permissionStatus.addEventListener("change", onChange);
      } catch {
        if (!cancelled) {
          setMicPermission("prompt");
        }
      }
    })();
    return () => {
      cancelled = true;
      permissionStatus?.removeEventListener("change", onChange);
    };
  }, []);

  const requestMic = useHandler(async () => {
    setMicError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const track of stream.getTracks()) {
        track.stop();
      }
      setMicPermission("granted");
    } catch (err) {
      const name = (err as { name?: string })?.name;
      if (name === "NotAllowedError") {
        setMicPermission("denied");
        setMicError(
          "Browser blocked microphone access. Open the site permissions and unblock the microphone, then reload.",
        );
      } else {
        setMicError(err instanceof Error ? err.message : String(err));
      }
    }
  });

  // ── Stats polling: detect bridged once we see inbound RTP ─────────────
  const stopStatsPolling = useCallback(() => {
    if (statsPollRef.current != null) {
      window.clearInterval(statsPollRef.current);
      statsPollRef.current = null;
    }
  }, []);

  const startStatsPolling = useCallback(
    (call: TelnyxCallLike) => {
      stopStatsPolling();
      const pc = getPeerConnection(call);
      if (pc == null) {
        return;
      }
      let lastBytes = 0;
      statsPollRef.current = window.setInterval(() => {
        if (callRef.current !== call) {
          stopStatsPolling();
          return;
        }
        void pc.getStats().then((report) => {
          for (const stat of report.values()) {
            if (
              stat.type === "inbound-rtp" &&
              (stat as { kind?: string }).kind === "audio"
            ) {
              const bytes = (stat as { bytesReceived?: number }).bytesReceived;
              if (typeof bytes === "number" && bytes > lastBytes) {
                if (lastBytes === 0 && bytes > 0) {
                  setActiveCall((cur) =>
                    cur
                      ? { ...cur, bridged: true, startedAt: new Date() }
                      : cur,
                  );
                  stopStatsPolling();
                  return;
                }
                lastBytes = bytes;
              }
            }
          }
        });
      }, 250);
    },
    [stopStatsPolling],
  );

  useEffect(() => stopStatsPolling, [stopStatsPolling]);

  // ── Availability publishing + heartbeat ───────────────────────────────
  const pushAvailability = useHandler(
    (status: "available" | "in-call" | "unavailable") => {
      setAvailability(status === "unavailable" ? null : status);
      setVoiceAvailability({ status, targetMongoUserId }).catch((err) => {
        console.error("setVoiceAvailability failed", { status, err });
      });
    },
  );

  useEffect(() => {
    if (availability == null) {
      return;
    }
    const id = window.setInterval(() => {
      setVoiceAvailability({ status: availability, targetMongoUserId }).catch(
        (err) => {
          console.error("setVoiceAvailability heartbeat failed", {
            status: availability,
            err,
          });
        },
      );
    }, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [availability, targetMongoUserId, setVoiceAvailability]);

  // ── Ringtone: play while a call is incoming ──────────────────────────
  useEffect(() => {
    if (incoming != null) {
      REDO_RINGTONE.play().catch((err) => {
        console.warn("Failed to play ringing sound:", err);
      });
    } else {
      REDO_RINGTONE.pause();
      REDO_RINGTONE.currentTime = 0;
    }
  }, [incoming]);

  // ── Customer lookup helper ────────────────────────────────────────────
  const lookupAndStashCustomer = useHandler(
    async (phoneNumber: string | null) => {
      if (!phoneNumber) {
        return;
      }
      try {
        const customer = await lookupCustomerByPhone({ phoneNumber });
        if (customer == null) {
          return;
        }
        const mappedCustomer: CallCustomer = {
          mongoCustomerId: customer.mongoCustomerId,
          name: customer.name,
          imageUrl: customer.imageUrl,
        };
        setActiveCall((cur) =>
          cur && cur.remoteNumber === phoneNumber
            ? { ...cur, customer: mappedCustomer }
            : cur,
        );
        setIncoming((cur) =>
          cur && cur.fromNumber === phoneNumber
            ? cur // incoming display reads fromNumber + customer separately if needed
            : cur,
        );
      } catch (err) {
        console.warn("Customer lookup failed:", err);
      }
    },
  );

  // ── Telnyx session lifecycle ──────────────────────────────────────────
  const connect = useHandler(async () => {
    setDesiredOnline(true);
    try {
      setConnectionStatus("fetching-token");
      setStatusDetail(null);
      setDebugTelnyxNotifications([]);
      const token = await getTelnyxToken({ targetMongoUserId });

      setConnectionStatus("connecting");
      const session = new TelnyxRTC({ login_token: token });
      session.on("telnyx.ready", () => {
        setConnectionStatus("ready");
        setStatusDetail(null);
        pushAvailability("available");
      });
      session.on("telnyx.error", (err: unknown) => {
        setConnectionStatus("error");
        setStatusDetail(String((err as { message?: string })?.message ?? err));
        pushAvailability("unavailable");
      });
      session.on("telnyx.socket.close", () => {
        setConnectionStatus("disconnected");
        setStatusDetail("socket closed");
        pushAvailability("unavailable");
      });

      session.on("telnyx.notification", (notification: unknown) => {
        const n = notification as {
          type?: string;
          call?: TelnyxCallLike & {
            state?: string;
            id?: string;
            callID?: string;
            callId?: string;
            options?: {
              customHeaders?: readonly { name: string; value: string }[];
              remoteCallerNumber?: string;
              remoteCallerName?: string;
            };
          };
        };
        const headers = n.call?.options?.customHeaders ?? [];
        const messageIdRaw =
          headers.find((h) => h.name === "X-Redo-Message-Id")?.value ?? null;
        const fromNumber =
          headers.find((h) => h.name === "X-Redo-From-Number")?.value ??
          n.call?.options?.remoteCallerNumber ??
          n.call?.options?.remoteCallerName ??
          null;
        setDebugTelnyxNotifications((prev) =>
          [
            {
              id: ++debugNotificationIdRef.current,
              receivedAt: new Date(),
              type: n.type ?? "unknown",
              callState: n.call?.state ?? null,
              callId: n.call?.callID ?? n.call?.callId ?? n.call?.id ?? null,
              fromNumber,
              messageId: messageIdRaw,
              isDebugPing: headers.some(
                (h) => h.name === "X-Redo-Voice-Debug-Ping",
              ),
              customHeaders: headers,
            },
            ...prev,
          ].slice(0, MAX_DEBUG_TELNYX_NOTIFICATIONS),
        );
        if (n?.type !== "callUpdate" || !n.call?.state) {
          return;
        }
        const call = n.call;
        const state = call.state;

        if (state === "ringing" && callRef.current !== call) {
          let messageId: MessageId | null = null;
          if (messageIdRaw) {
            try {
              messageId = MessageId.make(messageIdRaw);
            } catch (err) {
              console.warn(
                "Invalid X-Redo-Message-Id header, hold/record disabled for this call",
                { messageIdRaw, err },
              );
            }
          }
          setIncoming({ messageId, fromNumber, call });
          showWidget();
          if (fromNumber) {
            void lookupAndStashCustomer(fromNumber);
          }
          return;
        }

        if (callRef.current === call) {
          setActiveCall((cur) =>
            cur ? { ...cur, state: state ?? null } : cur,
          );
          if (state === "active") {
            startStatsPolling(call);
            pushAvailability("in-call");
            setRemoteStream(call.remoteStream);
          }
        }

        if (state !== undefined && TERMINAL_CALL_STATES.has(state)) {
          setIncoming((cur) => (cur?.call === call ? null : cur));
          if (callRef.current === call) {
            callRef.current = null;
            setActiveCall(null);
            setMuted(false);
            setDeafened(false);
            setOnHold(false);
            setRecording(false);
            setRemoteStream(undefined);
            stopStatsPolling();
            if (telnyxRef.current != null) {
              pushAvailability("available");
            }
          }
        }
      });

      await session.connect();
      telnyxRef.current = session;
    } catch (err) {
      setConnectionStatus("error");
      setStatusDetail(err instanceof Error ? err.message : String(err));
      pushAvailability("unavailable");
    }
  });

  const disconnect = useHandler(async () => {
    setDesiredOnline(false);
    const session = telnyxRef.current;
    telnyxRef.current = null;
    callRef.current = null;
    setActiveCall(null);
    setIncoming(null);
    setMuted(false);
    setDeafened(false);
    setOnHold(false);
    setRecording(false);
    setRemoteStream(undefined);
    setAvailability(null);
    setDebugTelnyxNotifications([]);
    stopStatsPolling();
    if (session) {
      try {
        await session.disconnect();
      } catch {
        // Ignore disconnect errors on teardown.
      }
    }
    setVoiceAvailability({ status: "unavailable", targetMongoUserId }).catch(
      (err) => {
        console.error("setVoiceAvailability(unavailable) failed", err);
      },
    );
    setConnectionStatus("disconnected");
    setStatusDetail(null);
  });

  // Tear down session on unmount so panels don't leak availability rows.
  useEffect(() => {
    return () => {
      const session = telnyxRef.current;
      telnyxRef.current = null;
      if (session) {
        try {
          void session.disconnect();
        } catch {
          // best-effort
        }
      }
    };
  }, []);

  // Switching the acting user requires a fresh telephony token. Tear
  // the session down — operator can reconnect via the status dropdown.
  const handleSetTargetMongoUserId = useHandler(
    (id: MongoUserId | undefined) => {
      if (id === targetMongoUserId) {
        return;
      }
      if (telnyxRef.current != null) {
        void disconnect();
      }
      setTargetMongoUserId(id);
    },
  );

  // ── Call actions ─────────────────────────────────────────────────────
  const placeOutbound = useHandler(async (phoneNumber: string) => {
    const trimmed = phoneNumber.trim();
    if (!trimmed) {
      return;
    }
    setIsPlacingOutbound(true);
    const session = telnyxRef.current;
    if (!session) {
      // Operator is offline — flip them to Available, queue the dial,
      // and the connection-watching useEffect fires it once the
      // session is ready. Outbound shouldn't require a manual
      // "go online" step first.
      setPendingOutboundNumber(trimmed);
      void connect();
      return;
    }
    try {
      const resp = await prepareOutboundCall({ customerPhoneNumber: trimmed });
      const telnyxCall = session.newCall({
        destinationNumber: resp.toPhoneNumber,
        callerNumber: resp.fromPhoneNumber,
        audio: true,
        video: false,
        clientState: resp.clientState,
      }) as unknown as TelnyxCallLike;
      callRef.current = telnyxCall;
      setActiveCall({
        messageId: resp.messageId,
        direction: "outbound",
        remoteNumber: resp.toPhoneNumber,
        customer: null,
        state: "new",
        startedAt: null,
        bridged: false,
      });
      void lookupAndStashCustomer(resp.toPhoneNumber);
    } catch (err) {
      setStatusDetail(err instanceof Error ? err.message : String(err));
      setIsPlacingOutbound(false);
    }
  });

  // Once an active call is established (or any prior call ends), the
  // dial flow is over — drop the "Calling…" lock so the dialer button
  // re-enables for the next call.
  useEffect(() => {
    if (activeCall != null) {
      setIsPlacingOutbound(false);
    }
  }, [activeCall]);

  // Fire any queued outbound dial as soon as the Telnyx session
  // becomes ready. `placeOutbound` queues a number when called while
  // offline; this effect drains the queue once `connect()` finishes.
  useEffect(() => {
    if (connectionStatus !== "ready") {
      return;
    }
    if (pendingOutboundNumber == null) {
      return;
    }
    const number = pendingOutboundNumber;
    setPendingOutboundNumber(null);
    void placeOutbound(number);
  }, [connectionStatus, pendingOutboundNumber, placeOutbound]);

  // Subscribe to "call this customer" intents fired from outside the
  // widget tree (e.g., the support ticket voice thread's call-back
  // button). Pop the widget open and prefill the dialer — the
  // operator hits Call themselves. We don't auto-dial because the
  // operator may want to verify the number, change identity, or just
  // not place the call after all.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<VoiceCallCustomerDetail>).detail;
      if (!detail?.phoneNumber) {
        return;
      }
      setDialerNumber(detail.phoneNumber);
      setWidgetExpanded(true);
      showWidget();
    };
    window.addEventListener(VOICE_CALL_CUSTOMER_EVENT, handler);
    return () => window.removeEventListener(VOICE_CALL_CUSTOMER_EVENT, handler);
  }, [showWidget]);

  const acceptIncoming = useHandler(() => {
    const current = incoming;
    if (!current) {
      return;
    }
    try {
      current.call.answer();
      callRef.current = current.call;
      setActiveCall({
        messageId: current.messageId,
        direction: "inbound",
        remoteNumber: current.fromNumber ?? "unknown",
        customer: null,
        state: "answering",
        startedAt: null,
        bridged: false,
      });
      setIncoming(null);
      if (current.fromNumber) {
        void lookupAndStashCustomer(current.fromNumber);
      }
    } catch (err) {
      setStatusDetail(err instanceof Error ? err.message : String(err));
    }
  });

  const rejectIncoming = useHandler(() => {
    const current = incoming;
    if (current) {
      try {
        current.call.hangup();
      } catch {
        // SDK may have already torn it down.
      }
    }
    setIncoming(null);
  });

  const hangup = useHandler(() => {
    const call = callRef.current;
    if (call) {
      try {
        call.hangup();
      } catch {
        // best-effort
      }
    }
    callRef.current = null;
    setActiveCall(null);
    setMuted(false);
    setDeafened(false);
    setOnHold(false);
    setRecording(false);
    setRemoteStream(undefined);
    stopStatsPolling();
  });

  // ── Call modifiers ───────────────────────────────────────────────────
  const toggleMute = useHandler(() => {
    const call = callRef.current;
    if (!call) {
      return;
    }
    try {
      call.toggleAudioMute?.();
      setMuted((m) => !m);
    } catch (err) {
      console.error("toggleMute failed", err);
    }
  });

  const toggleDeafen = useHandler(() => {
    const call = callRef.current;
    if (!call) {
      return;
    }
    try {
      call.toggleDeaf?.();
      setDeafened((d) => !d);
    } catch (err) {
      console.error("toggleDeafen failed", err);
    }
  });

  // Hold and recording optimistically flip local state, then call
  // effect-api. Revert on error so the UI reflects reality.
  const toggleHold = useHandler(() => {
    const messageId = activeCall?.messageId;
    if (messageId == null) {
      return;
    }
    const next = !onHold;
    setOnHold(next);
    setCallHold({ messageId, onHold: next }).catch((err) => {
      console.error("setCallHold failed", err);
      setOnHold((cur) => !cur);
    });
  });

  const toggleRecord = useHandler(() => {
    const messageId = activeCall?.messageId;
    if (messageId == null) {
      return;
    }
    const next = !recording;
    setRecording(next);
    setCallRecording({ messageId, recording: next }).catch((err) => {
      console.error("setCallRecording failed", err);
      setRecording((cur) => !cur);
    });
  });

  const sendDtmf = useHandler((digit: string) => {
    const call = callRef.current;
    if (!call) {
      return;
    }
    try {
      call.dtmf?.(digit);
    } catch (err) {
      console.error("sendDtmf failed", err);
    }
  });

  // ── Conference participants polling ──────────────────────────────────
  // Poll every 3s while a call is active so warm-transfer joins/leaves
  // surface in the UI. Empty list whenever there's no active call.
  const activeMessageId = activeCall?.messageId ?? null;
  useEffect(() => {
    if (activeMessageId == null) {
      setParticipants([]);
      setActiveSupportTicketId(null);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await getConferenceParticipants({
          messageId: activeMessageId,
        });
        if (!cancelled) {
          setParticipants(next.participants);
          setActiveSupportTicketId(next.supportTicketId);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("getConferenceParticipants failed", err);
        }
      }
    };
    void tick();
    const interval = window.setInterval(() => {
      void tick();
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [activeMessageId, getConferenceParticipants]);

  // ── Auto-mute + deafen on forwarded-phone connect ───────────────────
  // When the agent has a saved `forwardingPhoneNumber` and the dialed
  // phone leg joins the conference, mute + deafen the WebRTC browser
  // session so the agent only hears audio on their phone. Tracked by
  // messageId so a manual un-mute doesn't get reverted on the next
  // poll.
  //
  // `MongoUserId` is a prefixed branded form (`m_us_<hex>`); `user._id`
  // is the raw 24-char hex from Mongo. Convert the latter via
  // `MongoUserId.fromString` so the equality check works.
  const ownMongoId = useMemo<MongoUserId | null>(() => {
    if (targetMongoUserId !== undefined) {
      return targetMongoUserId;
    }
    if (!user?._id) {
      return null;
    }
    try {
      return MongoUserId.fromString(user._id);
    } catch {
      return null;
    }
  }, [targetMongoUserId, user]);

  useEffect(() => {
    const messageId = activeCall?.messageId;
    if (messageId == null) {
      autoMutedForMessageIdRef.current = null;
      return;
    }
    if (autoMutedForMessageIdRef.current === messageId) {
      return;
    }
    if (ownMongoId == null) {
      return;
    }
    const ownPhoneJoined = participants.some(
      (p) => p.kind === "forwarded_phone" && p.mongoUserId === ownMongoId,
    );
    if (!ownPhoneJoined) {
      return;
    }
    autoMutedForMessageIdRef.current = messageId;
    const call = callRef.current;
    if (call == null) {
      return;
    }
    try {
      if (!muted) {
        call.toggleAudioMute?.();
      }
      setMuted(true);
      if (!deafened) {
        call.toggleDeaf?.();
      }
      setDeafened(true);
    } catch (err) {
      console.error("auto-mute on forwarded-phone connect failed", err);
    }
  }, [activeCall, participants, ownMongoId, muted, deafened]);

  // Clear the ringing placeholder once the new leg actually shows up in
  // the participants list (by joined count growth) or after a hard cap.
  const previousParticipantCountRef = useRef(0);
  useEffect(() => {
    if (transferRinging == null) {
      previousParticipantCountRef.current = participants.length;
      return;
    }
    if (participants.length > previousParticipantCountRef.current) {
      setTransferRinging(null);
      if (transferRingingClearTimerRef.current != null) {
        window.clearTimeout(transferRingingClearTimerRef.current);
        transferRingingClearTimerRef.current = null;
      }
    }
    previousParticipantCountRef.current = participants.length;
  }, [participants, transferRinging]);

  const armTransferRingingTimeout = useCallback(() => {
    if (transferRingingClearTimerRef.current != null) {
      window.clearTimeout(transferRingingClearTimerRef.current);
    }
    transferRingingClearTimerRef.current = window.setTimeout(() => {
      setTransferRinging(null);
      transferRingingClearTimerRef.current = null;
    }, 30_000);
  }, []);

  useEffect(
    () => () => {
      if (transferRingingClearTimerRef.current != null) {
        window.clearTimeout(transferRingingClearTimerRef.current);
      }
    },
    [],
  );

  const addAgentToCall = useHandler(
    (targetMongoUserId: MongoUserId, targetLabel: string) => {
      const messageId = activeCall?.messageId;
      if (messageId == null) {
        return;
      }
      previousParticipantCountRef.current = participants.length;
      setTransferRinging({ targetLabel });
      armTransferRingingTimeout();
      addAgentToCallRpc({ messageId, targetMongoUserId }).catch((err) => {
        console.error("addAgentToCall failed", err);
        setTransferRinging(null);
      });
    },
  );

  const addPhoneNumberToCall = useHandler((phoneNumber: string) => {
    const messageId = activeCall?.messageId;
    if (messageId == null) {
      return;
    }
    previousParticipantCountRef.current = participants.length;
    setTransferRinging({ targetLabel: phoneNumber });
    armTransferRingingTimeout();
    addPhoneNumberToCallRpc({ messageId, phoneNumber }).catch((err) => {
      console.error("addPhoneNumberToCall failed", err);
      setTransferRinging(null);
    });
  });

  const value: VoiceV2State = useMemo(
    () => ({
      connectionStatus,
      statusDetail,
      connect,
      disconnect,
      desiredOnline,
      micPermission,
      micError,
      requestMic,
      incoming,
      activeCall,
      acceptIncoming,
      rejectIncoming,
      placeOutbound,
      hangup,
      muted,
      deafened,
      onHold,
      recording,
      toggleMute,
      toggleDeafen,
      toggleHold,
      toggleRecord,
      sendDtmf,
      availability,
      participants,
      activeSupportTicketId,
      dialerNumber,
      setDialerNumber,
      widgetExpanded,
      setWidgetExpanded,
      isPlacingOutbound,
      transferRinging,
      addAgentToCall,
      addPhoneNumberToCall,
      targetMongoUserId,
      setTargetMongoUserId: handleSetTargetMongoUserId,
      debugTelnyxNotifications,
    }),
    [
      connectionStatus,
      statusDetail,
      connect,
      disconnect,
      desiredOnline,
      micPermission,
      micError,
      requestMic,
      incoming,
      activeCall,
      acceptIncoming,
      rejectIncoming,
      placeOutbound,
      hangup,
      muted,
      deafened,
      onHold,
      recording,
      toggleMute,
      toggleDeafen,
      toggleHold,
      toggleRecord,
      sendDtmf,
      availability,
      participants,
      activeSupportTicketId,
      dialerNumber,
      widgetExpanded,
      isPlacingOutbound,
      transferRinging,
      addAgentToCall,
      addPhoneNumberToCall,
      targetMongoUserId,
      handleSetTargetMongoUserId,
      debugTelnyxNotifications,
    ],
  );

  return (
    <VoiceContextV2.Provider value={value}>
      {children}
      {/*
        Without an <Audio> element bound to the remote stream, outbound
        calls have no audio playback (the v1 widget does this — see
        merchant/support/app/src/voice/voice-widget.tsx). Mounted as a
        sibling to children so it lives for the provider's lifetime.
      */}
      {remoteStream && <Audio stream={remoteStream} />}
    </VoiceContextV2.Provider>
  );
});
